/**
 * app/providers/SessionProvider — Bootstrap the session store on app load.
 *
 * Imports the session store (which wires the token accessor + 401 handler
 * as a module-level side effect) and calls bootstrap() once on mount.
 *
 * The session store's bootstrap() calls GET /api/auth/me/ using any existing
 * httpOnly refresh cookie; on 401 it tries POST /api/auth/refresh/ (cookie-based),
 * then retries /me. On failure it sets status = 'unauthenticated'.
 *
 * This provider must be mounted INSIDE QueryProvider and RouterProvider so that
 * the bootstrap runs in the right context.
 */

import { useEffect, type ReactNode } from 'react';
import { useSessionStore } from '@/entities/session';

interface SessionProviderProps {
  children: ReactNode;
}

export function SessionProvider({ children }: SessionProviderProps) {
  const bootstrap = useSessionStore((s) => s.bootstrap);
  const status = useSessionStore((s) => s.status);

  useEffect(() => {
    if (status === 'idle') {
      void bootstrap();
    }
  }, [bootstrap, status]);

  return <>{children}</>;
}
